import { loadDomFill2Container } from '../ui'

export interface UploadBoxOptions {
  accept?: string
  maxSize?: number
  multiple?: boolean
  onBefore?: () => boolean | void
  onSuccess?: (files: FileList) => void
  onError?: (invalidFiles: Array<{ name: string; reason: string }>) => void
}

export interface UploadOptions {
  url: string
  file: File
  fieldName?: string
  data?: Record<string, string>
  headers?: Record<string, string>
  withCredentials?: boolean
  onProgress?: (percent: number) => void
}

export interface UploadResponse {
  status: number
  data: any
  headers: Record<string, string>
}

/**
 * 上传文件
 * @description 使用XMLHttpRequest上传文件，支持进度回调
 * @param {UploadOptions} options - 上传选项
 * @param {string} options.url - 上传地址
 * @param {File} options.file - 要上传的文件
 * @param {string} options.fieldName - 表单字段名，默认'file'
 * @param {Record<string, string>} options.data - 附加参数，将作为JSON对象发送
 * @param {Record<string, string>} options.headers - 自定义请求头
 * @param {boolean} options.withCredentials - 是否携带凭证
 * @param {(percent: number) => void} options.onProgress - 进度回调函数
 * @returns {Promise<UploadResponse>} 上传结果
 * @example
 * const result = await upload({
 *   url: '/api/upload',
 *   file: fileInput.files[0],
 *   fieldName: 'avatar',
 *   data: {
 *     userId: '123',
 *     category: 'avatar'
 *   },
 *   onProgress: (percent) => {
 *     console.log(`上传进度: ${percent}%`)
 *   }
 * })
 * console.log(result.data)
 */
export async function upload(options: UploadOptions): Promise<UploadResponse> {
  const {
    url,
    file,
    fieldName = 'file',
    data = {},
    headers = {},
    withCredentials = false,
    onProgress,
  } = options

  const formData = new FormData()
  formData.append(fieldName, file)

  if (Object.keys(data).length > 0) {
    formData.append('data', JSON.stringify(data))
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.withCredentials = withCredentials

    xhr.upload.addEventListener('progress', (e) => {
      if (onProgress && e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100)
        onProgress(percent)
      }
    })

    xhr.addEventListener('load', () => {
      const responseHeaders: Record<string, string> = {}
      xhr
        .getAllResponseHeaders()
        .split('\n')
        .forEach((line) => {
          const [key, value] = line.split(': ')
          if (key && value) {
            responseHeaders[key] = value.trim()
          }
        })

      let responseData: any
      try {
        responseData = JSON.parse(xhr.responseText)
      } catch {
        responseData = xhr.responseText
      }

      resolve({
        status: xhr.status,
        data: responseData,
        headers: responseHeaders,
      })
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    xhr.open('POST', url)

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    xhr.send(formData)
  })
}

/**
 * 带重试的文件上传
 * @description 上传失败时自动重试
 * @param {UploadOptions} options - 上传选项
 * @param {number} maxRetries - 最大重试次数，默认3次
 * @returns {Promise<UploadResponse>} 上传结果
 * @example
 * const result = await uploadWithRetry({
 *   url: '/api/upload',
 *   file: file
 * }, 5)
 */
export function uploadWithRetry(options: UploadOptions, maxRetries: number = 3): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    let retries = 0

    const attempt = () => {
      upload(options)
        .then(resolve)
        .catch((error) => {
          retries++
          if (retries < maxRetries) {
            setTimeout(attempt, 1000 * retries)
          } else {
            reject(error)
          }
        })
    }

    attempt()
  })
}

/**
 * 批量上传文件
 * @description 并发上传多个文件，支持控制并发数
 * @param {File[]} files - 文件数组
 * @param {Omit<UploadOptions, 'file'>} options - 上传选项（不含file）
 * @param {number} concurrency - 并发数，默认3
 * @returns {Promise<UploadResponse[]>} 上传结果数组
 * @example
 * const files = [file1, file2, file3, file4, file5]
 * const results = await uploadMultiple(files, {
 *   url: '/api/upload'
 * }, 2)
 */
export function uploadMultiple(
  files: File[],
  options: Omit<UploadOptions, 'file'>,
  concurrency: number = 3
): Promise<UploadResponse[]> {
  return new Promise((resolve, reject) => {
    const results: UploadResponse[] = []
    let current = 0
    let completed = 0

    const runNext = () => {
      if (completed >= files.length) {
        resolve(results)
        return
      }

      while (current < files.length && current - completed < concurrency) {
        const index = current
        current++

        upload({ ...options, file: files[index] })
          .then((result) => {
            results[index] = result
            completed++
            runNext()
          })
          .catch((error) => {
            reject(error)
          })
      }
    }

    runNext()
  })
}

/**
 * 创建上传器
 * @description 创建一个预配置的上传器实例
 * @param {Omit<UploadOptions, 'file'>} options - 上传选项
 * @returns {object} 上传器对象
 * @example
 * const uploader = createUploader({
 *   url: '/api/upload'
 * })
 *
 * // 上传单个文件
 * await uploader.upload(file, (percent) => {
 *   console.log(percent)
 * })
 *
 * // 上传多个文件
 * await uploader.uploadMultiple([file1, file2])
 */
export function createUploader(options: Omit<UploadOptions, 'file'>) {
  return {
    upload: (file: File, onProgress?: (percent: number) => void) =>
      upload({ ...options, file, onProgress }),
    uploadMultiple: (files: File[], onProgress?: (index: number, percent: number) => void) => {
      return uploadMultiple(files, {
        ...options,
        onProgress: (_percent) => {
          onProgress?.(0, 0)
        },
      })
    },
  }
}

/**
 * 验证文件
 * @description 验证文件大小、类型、尺寸等
 * @param {File} file - 要验证的文件
 * @param {object} options - 验证选项
 * @param {number} options.maxSize - 最大文件大小（字节）
 * @param {string[]} options.allowedTypes - 允许的MIME类型
 * @param {number} options.minWidth - 最小宽度（图片）
 * @param {number} options.minHeight - 最小高度（图片）
 * @param {number} options.maxWidth - 最大宽度（图片）
 * @param {number} options.maxHeight - 最大高度（图片）
 * @returns {Promise<boolean>} 是否通过验证
 * @example
 * const isValid = await validateFile(file, {
 *   maxSize: 10 * 1024 * 1024, // 10MB
 *   allowedTypes: ['image/jpeg', 'image/png'],
 *   maxWidth: 1920,
 *   maxHeight: 1080
 * })
 */
export function validateFile(
  file: File,
  options: {
    maxSize?: number
    allowedTypes?: string[]
    minWidth?: number
    minHeight?: number
    maxWidth?: number
    maxHeight?: number
  }
): Promise<boolean> {
  return new Promise((resolve) => {
    if (options.maxSize && file.size > options.maxSize) {
      resolve(false)
      return
    }

    if (options.allowedTypes && !options.allowedTypes.includes(file.type)) {
      resolve(false)
      return
    }

    if (file.type.startsWith('image/') && (options.minWidth || options.minHeight || options.maxWidth || options.maxHeight)) {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        if (options.minWidth && img.width < options.minWidth) {
          resolve(false)
          return
        }
        if (options.minHeight && img.height < options.minHeight) {
          resolve(false)
          return
        }
        if (options.maxWidth && img.width > options.maxWidth) {
          resolve(false)
          return
        }
        if (options.maxHeight && img.height > options.maxHeight) {
          resolve(false)
          return
        }
        resolve(true)
      }
      img.onerror = () => resolve(false)
      img.src = URL.createObjectURL(file)
    } else {
      resolve(true)
    }
  })
}

/**
 * 获取文件扩展名
 * @description 从文件名中提取扩展名（小写）
 * @param {string} filename - 文件名
 * @returns {string} 扩展名
 * @example
 * getFileExtension('document.PDF') // 'pdf'
 * getFileExtension('image.png') // 'png'
 */
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase()
}

/**
 * 获取文件图标
 * @description 根据文件类型返回对应的emoji图标
 * @param {string} type - 文件类型/扩展名
 * @returns {string} emoji图标
 * @example
 * getFileIcon('pdf') // '📄'
 * getFileIcon('jpg') // '🖼️'
 * getFileIcon('mp3') // '🎵'
 */
export function getFileIcon(type: string): string {
  const icons: Record<string, string> = {
    pdf: '📄',
    doc: '📘',
    docx: '📘',
    xls: '📊',
    xlsx: '📊',
    ppt: '📙',
    pptx: '📙',
    zip: '📦',
    rar: '📦',
    '7z': '📦',
    mp3: '🎵',
    mp4: '🎬',
    avi: '🎬',
    mov: '🎬',
    jpg: '🖼️',
    jpeg: '🖼️',
    png: '🖼️',
    gif: '🖼️',
    svg: '🖼️',
  }
  return icons[type] || '📄'
}

/**
 * 格式化文件大小
 * @description 将字节数转换为可读的文件大小格式
 * @param {number} bytes - 字节数
 * @returns {string} 格式化后的文件大小
 * @example
 * formatFileSize(1024) // '1 KB'
 * formatFileSize(1048576) // '1 MB'
 * formatFileSize(1536) // '1.5 KB'
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export type UploadModel = 'formdataOneByOne' | 'formdataAllInOne'

export interface EasyUploadOptions {
  multiple?: boolean
  maxSize?: number
  accept?: string
  url: string
  data?: Record<string, string>
  headers?: Record<string, string>
  uploadModel?: UploadModel
  onSuccess?: (file: File, response: any) => void
  onError?: (file: File, error: Error) => void
  onComplete?: (results: Array<{ file: File; response: any; success: boolean }>) => void
}

interface UploadFileItem {
  id: string
  file: File
  name: string
  size: string
  progress: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
}

function generateId(): string {
  return 'prl-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)
}

/**
 * 傻瓜式上传组件
 * @doc true
 * @description 一键调用即可弹出上传窗口，支持选择文件、拖拽上传、进度显示、自动上传
 * @param {EasyUploadOptions} options - 上传配置选项
 * @param {boolean} options.multiple - 是否支持多文件选择，默认false
 * @param {number} options.maxSize - 单个文件最大大小（字节），默认10MB
 * @param {string} options.accept - 允许的文件类型，如'image/*'或'.jpg,.png'
 * @param {string} options.url - 上传目标地址
 * @param {Record<string, string>} options.data - 附加参数，随文件一起上传
 * @param {Record<string, string>} options.headers - 自定义请求头
 * @param {UploadModel} options.uploadModel - 上传模式，'formdataOneByOne'逐个上传或'formdataAllInOne'一次性上传，默认'formdataOneByOne'
 * @param {(file: File, response: any) => void} options.onSuccess - 单个文件上传成功回调
 * @param {(file: File, error: Error) => void} options.onError - 单个文件上传失败回调
 * @param {(results: Array) => void} options.onComplete - 所有文件上传完成回调
 * @returns {void}
 * @example
 * // 最简单调用
 * easyUpload({
 *   url: '/api/upload'
 * })
 *
 * // 完整配置
 * easyUpload({
 *   multiple: true,
 *   maxSize: 20 * 1024 * 1024,
 *   accept: 'image/*',
 *   url: '/api/upload',
 *   data: {
 *     userId: '123',
 *     category: 'avatar'
 *   },
 *   headers: {
 *     'Authorization': 'Bearer token123',
 *     'X-Custom-Header': 'custom-value'
 *   },
 *   uploadModel: 'formdataAllInOne',
 *   onSuccess: (file, response) => {
 *     console.log('上传成功:', file.name, response)
 *   },
 *   onError: (file, error) => {
 *     console.error('上传失败:', file.name, error)
 *   },
 *   onComplete: (results) => {
 *     console.log('全部完成:', results)
 *   }
 * })
 */
export function easyUpload(options: EasyUploadOptions): void {
  const {
    multiple = false,
    maxSize = 10 * 1024 * 1024,
    accept = '*',
    url,
    data = {},
    headers = {},
    uploadModel = 'formdataOneByOne',
    onSuccess,
    onError,
    onComplete,
  } = options

  const containerId = generateId()

  const files: UploadFileItem[] = []
  let isUploading = false

  function getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || ''
    const icons: Record<string, string> = {
      pdf: '📄',
      doc: '📘',
      docx: '📘',
      xls: '📊',
      xlsx: '📊',
      ppt: '📙',
      pptx: '📙',
      zip: '📦',
      rar: '📦',
      mp3: '🎵',
      mp4: '🎬',
      avi: '🎬',
      mov: '🎬',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      svg: '🖼️',
    }
    return icons[ext] || '📄'
  }

  function formatSize(bytes: number): string {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  function createModal(): void {
    const container = document.createElement('div')
    container.id = `prl-upload-container-${containerId}`
    document.body.appendChild(container)

    const templatePath = '/file/template/upload/easyupload.html'
    const result = loadDomFill2Container(templatePath, container.id, () => {
      const fileInput = container.querySelector('.prl-file-input') as HTMLInputElement
      const dropHint = container.querySelector('.prl-drop-hint') as HTMLElement
      
      if (dropHint) {
        dropHint.textContent = `${multiple ? '支持选择多个文件' : '点击选择文件'}，最大 ${formatSize(maxSize)}`
      }
      
      if (fileInput) {
        if (multiple) {
          fileInput.setAttribute('multiple', 'multiple')
        }
        fileInput.setAttribute('accept', accept)
      }

      bindEvents(container)
    })
    
    container.setAttribute('data-', result.dataAttr)
  }

  function bindEvents(container: HTMLElement): void {
    const overlay = container.querySelector('.prl-overlay') as HTMLElement
    const closeBtn = container.querySelector('.prl-close') as HTMLElement
    const dropArea = container.querySelector('.prl-drop-area') as HTMLElement
    const fileInput = container.querySelector('.prl-file-input') as HTMLInputElement
    const clearBtn = container.querySelector('.prl-clear-btn') as HTMLElement
    const uploadBtn = container.querySelector('.prl-upload-btn') as HTMLElement

    closeBtn.addEventListener('click', () => {
      if (!isUploading) {
        destroy()
      }
    })

    overlay.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('prl-overlay')) {
        if (!isUploading) {
          destroy()
        }
      }
    })

    dropArea.addEventListener('click', () => {
      fileInput.click()
    })

    dropArea.addEventListener('dragover', (e) => {
      e.preventDefault()
      dropArea.classList.add('prl-drag-over')
    })

    dropArea.addEventListener('dragleave', () => {
      dropArea.classList.remove('prl-drag-over')
    })

    dropArea.addEventListener('drop', (e) => {
      e.preventDefault()
      dropArea.classList.remove('prl-drag-over')
      handleFiles(e.dataTransfer?.files || new FileList())
    })

    fileInput.addEventListener('change', (e) => {
      handleFiles((e.target as HTMLInputElement).files || new FileList())
      fileInput.value = ''
    })

    clearBtn.addEventListener('click', () => {
      if (!isUploading) {
        files.length = 0
        renderFileList()
      }
    })

    uploadBtn.addEventListener('click', () => {
      startUpload()
    })
  }

  function handleFiles(fileList: FileList): void {
    const newFiles = Array.from(fileList)
    newFiles.forEach((file) => {
      if (file.size > maxSize) {
        alert(`文件 "${file.name}" 超过最大限制 (${formatSize(maxSize)})`)
        return
      }
      if (files.some((f) => f.name === file.name && f.file.size === file.size)) {
        return
      }
      files.push({
        id: generateId(),
        file,
        name: file.name,
        size: formatSize(file.size),
        progress: 0,
        status: 'pending',
      })
    })
    renderFileList()
  }

  function renderFileList(): void {
    const container = document.getElementById(`prl-upload-container-${containerId}`)
    if (!container) return

    const fileList = container.querySelector('.prl-file-list') as HTMLElement
    const countSpan = container.querySelector('.prl-count') as HTMLElement
    const uploadBtn = container.querySelector('.prl-upload-btn') as HTMLButtonElement

    if (!fileList || !countSpan || !uploadBtn) return

    countSpan.textContent = String(files.length)
    uploadBtn.disabled = files.length === 0 || isUploading

    if (files.length === 0) {
      fileList.innerHTML = ''
      return
    }

    const dataAttr = container.getAttribute('data-') || ''

    fileList.innerHTML = files
      .map(
        (item) => `
        <div class="prl-file-item" data-id="${item.id}" ${dataAttr}>
          <div class="prl-file-icon" ${dataAttr}>${getFileIcon(item.name)}</div>
          <div class="prl-file-info" ${dataAttr}>
            <div class="prl-file-name" ${dataAttr}>${item.name}</div>
            <div class="prl-file-size" ${dataAttr}>${item.size}</div>
            ${item.status === 'uploading' ? `
              <div class="prl-progress-wrap" ${dataAttr}>
                <div class="prl-progress-bar" ${dataAttr}>
                  <div class="prl-progress-fill" style="width:${item.progress}%" ${dataAttr}></div>
                </div>
              </div>
            ` : ''}
          </div>
          <div class="prl-file-status prl-status-${item.status}" ${dataAttr}>
            ${item.status === 'pending' ? '等待中' : item.status === 'uploading' ? `${item.progress}%` : item.status === 'success' ? '✓ 成功' : '✗ 失败'}
          </div>
          ${item.status === 'pending' ? `<button class="prl-remove-btn" data-id="${item.id}" ${dataAttr}>✕</button>` : ''}
        </div>
      `
      )
      .join('')

    fileList.querySelectorAll('.prl-remove-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const id = (e.target as HTMLElement).getAttribute('data-id')
        const index = files.findIndex((f) => f.id === id)
        if (index > -1) {
          files.splice(index, 1)
          renderFileList()
        }
      })
    })
  }

  async function startUpload(): Promise<void> {
    isUploading = true
    const container = document.getElementById(`prl-upload-container-${containerId}`)
    if (!container) return

    const uploadBtn = container.querySelector('.prl-upload-btn') as HTMLButtonElement
    uploadBtn.disabled = true
    uploadBtn.textContent = '上传中...'

    const results: Array<{ file: File; response: any; success: boolean }> = []

    if (uploadModel === 'formdataAllInOne') {
      await uploadAllInOne(results)
    } else {
      await uploadOneByOne(results)
    }

    isUploading = false
    uploadBtn.textContent = '上传完成'
    onComplete?.(results)

    setTimeout(() => {
      uploadBtn.textContent = '开始上传'
      uploadBtn.disabled = files.length === 0
    }, 2000)
  }

  async function uploadOneByOne(results: Array<{ file: File; response: any; success: boolean }>): Promise<void> {
    for (const item of files) {
      if (item.status !== 'pending') continue

      item.status = 'uploading'
      renderFileList()

      try {
        const response = await upload({
          url,
          file: item.file,
          data,
          headers,
          onProgress: (percent) => {
            item.progress = percent
            renderFileList()
          }
        })

        if (response.status !== 200) {
          throw new Error(`HTTP ${response.status}`)
        }

        const responseData = response.data
        item.status = 'success'
        item.progress = 100
        results.push({ file: item.file, response: responseData, success: true })
        onSuccess?.(item.file, responseData)
      } catch (error) {
        item.status = 'error'
        item.error = (error as Error).message
        results.push({ file: item.file, response: null, success: false })
        onError?.(item.file, error as Error)
      }

      renderFileList()
      await new Promise((resolve) => setTimeout(resolve, 300))
    }
  }

  async function uploadAllInOne(results: Array<{ file: File; response: any; success: boolean }>): Promise<void> {
    const pendingFiles = files.filter(item => item.status === 'pending')
    if (pendingFiles.length === 0) return

    pendingFiles.forEach(item => {
      item.status = 'uploading'
      item.progress = 0
    })
    renderFileList()

    try {
      const formData = new FormData()
      pendingFiles.forEach((item) => {
        formData.append('files', item.file)
      })

      if (Object.keys(data).length > 0) {
        formData.append('data', JSON.stringify(data))
      }

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100)
            pendingFiles.forEach(item => {
              item.progress = percent
            })
            renderFileList()
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            let responseData: any
            try {
              responseData = JSON.parse(xhr.responseText)
            } catch {
              responseData = xhr.responseText
            }

            pendingFiles.forEach(item => {
              item.status = 'success'
              item.progress = 100
              results.push({ file: item.file, response: responseData, success: true })
              onSuccess?.(item.file, responseData)
            })
            resolve()
          } else {
            throw new Error(`HTTP ${xhr.status}`)
          }
        })

        xhr.addEventListener('error', () => {
          pendingFiles.forEach(item => {
            item.status = 'error'
            item.error = 'Upload failed'
            results.push({ file: item.file, response: null, success: false })
            onError?.(item.file, new Error('Upload failed'))
          })
          reject(new Error('Upload failed'))
        })

        xhr.open('POST', url)

        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value)
        })

        xhr.send(formData)
      })
    } catch (error) {
      pendingFiles.forEach(item => {
        item.status = 'error'
        item.error = (error as Error).message
        results.push({ file: item.file, response: null, success: false })
        onError?.(item.file, error as Error)
      })
    }

    renderFileList()
  }

  function destroy(): void {
    const container = document.getElementById(`prl-upload-container-${containerId}`)
    if (container) {
      container.remove()
    }
  }

  createModal()
}

/**
 * 让一个普通dom具备点击打开上传对话框能力
 * @doc true
 * @description 为DOM实例绑定文件上传功能，点击后打开文件选择框
 * @param {HTMLElement} element - DOM实例
 * @param {UploadBoxOptions} options - 配置选项
 * @param {string} options.accept - 允许的文件类型，如'.jpg,.png'或'image/*'
 * @param {number} options.maxSize - 单个文件最大大小（字节）
 * @param {boolean} options.multiple - 是否支持多文件选择，默认false
 * @param {() => boolean | void} options.onBefore - 点击前回调，返回false可阻止打开文件选择框
 * @param {(files: FileList) => void} options.onSuccess - 文件选择成功回调
 * @param {(invalidFiles: Array<{ name: string; reason: string }>) => void} options.onError - 无效文件回调
 * @returns {void}
 * @example
 * set_upload_box(buttonElement, {
 *   accept: '.jpg,.png',
 *   maxSize: 10 * 1024 * 1024,
 *   multiple: true,
 *   onBefore: () => {
 *     console.log('准备打开文件选择框')
 *     return true
 *   },
 *   onSuccess: (files) => {
 *     console.log('选择的文件:', files)
 *   },
 *   onError: (invalidFiles) => {
 *     console.log('无效文件:', invalidFiles)
 *   }
 * })
 */
export function set_upload_box(element: HTMLElement, options: UploadBoxOptions = {}): void {
  const {
    accept = '*',
    maxSize = 10 * 1024 * 1024,
    multiple = false,
    onBefore,
    onSuccess,
    onError
  } = options

  let selectorId: string

  const existingSelector = element.getAttribute('data-file-selector')
  if (existingSelector && existingSelector.startsWith('file-selector-')) {
    selectorId = existingSelector
    const existingInput = document.querySelector(`input[data-file-selector="${selectorId}"]`) as HTMLInputElement
    if (existingInput) {
      const handler = inputHandlers.get(existingInput)
      if (handler) {
        existingInput.removeEventListener('change', handler)
        inputHandlers.delete(existingInput)
      }
      existingInput.remove()
    }
  } else {
    selectorId = `file-selector-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    element.setAttribute('data-file-selector', selectorId)
  }

  const input = document.createElement('input')
  input.type = 'file'
  input.style.display = 'none'
  input.setAttribute('data-file-selector', selectorId)
  input.accept = accept
  if (multiple) {
    input.setAttribute('multiple', 'multiple')
  }

  const changeHandler = (e: Event) => {
    const target = e.target as HTMLInputElement
    const files = target.files
    
    if (files && files.length > 0) {
      const validFiles: File[] = []
      const invalidFiles: { name: string; reason: string }[] = []

      Array.from(files).forEach(file => {
        if (file.size > maxSize) {
          invalidFiles.push({ name: file.name, reason: `超过大小限制 (${formatSizeBytes(maxSize)})` })
        } else {
          validFiles.push(file)
        }
      })

      if (invalidFiles.length > 0) {
        // console.warn('以下文件不符合要求:', invalidFiles)
        onError?.(invalidFiles)
      }
      
      if (validFiles.length > 0) {
        onSuccess?.(files)
      } else {
        console.warn('没有有效的文件，不触发onSuccess回调')
      }
    }
  }

  inputHandlers.set(input, changeHandler)
  input.addEventListener('change', changeHandler)

  const clickHandler = () => {
    if (onBefore) {
      const result = onBefore()
      if (result === false) {
        return
      }
    }
    input.click()
  }

  const existingClickHandler = elementClickHandlers.get(element)
  if (existingClickHandler) {
    element.removeEventListener('click', existingClickHandler)
  }

  elementClickHandlers.set(element, clickHandler)
  element.addEventListener('click', clickHandler)

  const parent = element.parentElement
  if (parent) {
    parent.insertBefore(input, element.nextSibling)
  } else {
    document.body.appendChild(input)
  }
}

const inputHandlers = new WeakMap<HTMLInputElement, EventListener>()
const elementClickHandlers = new WeakMap<HTMLElement, EventListener>()

function formatSizeBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
